import type { Bill } from '../services/bills-service';

export type ScheduleTone = 'info' | 'ok' | 'warn' | 'danger';

export interface ScheduleBadge {
  label: string;
  tone: ScheduleTone;
  /** Shown on hover: the verifier's note, or what the schedule is waiting on. */
  detail: string;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * "Oct 12" from "2026-10-12" (or an RFC 3339 timestamp at midnight UTC).
 * Read from the string, not via Date: a calendar day parsed as an instant
 * lands on the 11th anywhere west of Greenwich.
 */
export function shortDay(iso: string): string {
  const [, m, d] = iso.slice(0, 10).split('-').map(Number);
  return `${MONTHS[m - 1]} ${d}`;
}

/**
 * Why a needs_review bill stopped, in a few words. The server's note reads
 * "amount changed: scheduled 516.69, now 520.00"; the badge only needs the
 * part before the colon, and the full note goes in the tooltip.
 */
function reviewReason(note?: string | null): string {
  if (!note) return '';
  const reasons = note.split(';').map((n) => n.trim().split(':')[0]).filter(Boolean);
  return reasons.join(', ');
}

/**
 * The small badge a scheduled bill shows in its row, or null when the bill
 * has no schedule (or a scheduled one has since been paid by hand, where
 * "Scheduled for ..." would no longer be true).
 */
export function scheduleBadge(
  b: Pick<Bill, 'schedule_state' | 'scheduled_pay_on' | 'schedule_note' | 'scheduled_amount' | 'effective_status'>,
): ScheduleBadge | null {
  switch (b.schedule_state) {
    case 'scheduled': {
      if (b.effective_status === 'Paid' || !b.scheduled_pay_on) return null;
      const day = shortDay(b.scheduled_pay_on);
      return {
        label: `Scheduled for ${day}`,
        tone: 'info',
        detail: b.schedule_note
          ?? `Expected ${b.scheduled_amount ? `${b.scheduled_amount} ` : ''}on ${day}; `
            + 'marked paid automatically when the bank shows it.',
      };
    }
    case 'matched':
      return { label: 'Matched', tone: 'ok', detail: b.schedule_note ?? 'Matched to a posted transaction.' };
    case 'needs_review': {
      const why = reviewReason(b.schedule_note);
      return {
        label: why ? `Needs review: ${why}` : 'Needs review',
        tone: 'danger',
        detail: `${b.schedule_note ?? 'Changed after it was scheduled.'} Save the bill again to re-schedule.`,
      };
    }
    case 'not_seen':
      return { label: 'Not seen', tone: 'warn', detail: b.schedule_note ?? 'No matching payment has posted.' };
    default:
      return null;
  }
}
